import type { Namespace } from '../types';

// DE drafted — queued for native review in the task file's "DE review queue".
export const userManagement = {
  title: { de: 'Benutzerverwaltung', en: 'User Management' },
  desc: {
    de: 'Aktive Mitglieder des Standorts — Zeile anklicken für Details',
    en: 'Active members of the site — click a row for details',
  },

  colUser: { de: 'Benutzer', en: 'User' },

  editUser: { de: 'Benutzer bearbeiten', en: 'Edit user' },
  phFirstName: { de: 'z. B. Sara', en: 'e.g. Sara' },
  phLastName: { de: 'z. B. Al Mansoori', en: 'e.g. Al Mansoori' },
  phPhone: { de: 'z. B. +971 50 123 4567', en: 'e.g. +971 50 123 4567' },
  phHomeSite: { de: 'Standort wählen', en: 'Select a site' },
  loadedCount: { de: '{shown} von {total} Benutzern geladen', en: '{shown} of {total} users loaded' },
  filterType: { de: 'Alle Typen', en: 'All types' },
  filterStatus: { de: 'Alle Status', en: 'All statuses' },
  noUsersMatch: { de: 'Keine Benutzer passen zu diesen Filtern', en: 'No users match these filters' },
  loadMore: { de: 'Mehr laden', en: 'Load more' },
  detailsTitle: { de: 'Benutzerdetails', en: 'User details' },
  editDetailsTitle: { de: 'Benutzerdetails bearbeiten', en: 'Edit user details' },
  resetPassword: { de: 'Passwort zurücksetzen', en: 'Reset password' },
  deleteUser: { de: 'Benutzer löschen', en: 'Delete user' },
  resetMfa: { de: '2FA zurücksetzen', en: 'Reset 2FA' },
  resetMfaTitle: { de: '2FA zurücksetzen — {name}', en: 'Reset 2FA — {name}' },
  resetMfaMsg: {
    de: 'Entfernt die Authenticator-App des Benutzers. Beim nächsten Login richtet er eine neue ein.',
    en: "Removes the user's authenticator app. They set up a new one at their next sign-in.",
  },
  toastMfaReset: { de: '2FA zurückgesetzt.', en: '2FA reset.' },

  saveMsg: {
    de: 'Details und Zugriff von {email} aktualisieren.',
    en: "Update {email}'s details and access.",
  },
  // ⚠ The home site is an ACCESS grant, not a profile detail: the site refuses a
  // non-admin whose home is not that site, and refuses one with no home at every
  // site. The copy has to say that plainly, or an admin leaves it blank and the
  // person appears to have working roles and no access.
  homeSite: { de: 'Standort (Zugriff)', en: 'Home site (access)' },
  homeSiteHint: {
    de: 'Diese Person kann nur auf diesen Standort zugreifen.',
    en: 'This person can reach only this site.',
  },
  // R-348 — the same two controls the invite dialog shows, so the two screens
  // describe one model. The copy is deliberately identical to `registration`.
  worksAt: { de: 'Arbeitet bei', en: 'Works at' },
  scopeHq: { de: 'Hauptverwaltung', en: 'Headquarters' },
  scopeSite: { de: 'Ein Standort', en: 'A site' },
  hqHint: {
    de: 'Sieht ALLE Standorte, mit den zugewiesenen Rollen.',
    en: 'Sees every site, with the roles assigned here.',
  },
  // ⚠ Replaces `homeSiteNone` + `homeSiteWarn`. "— no site —" was a way to
  // produce the lockout from the very screen that exists to fix it: a member
  // with no site is refused everywhere. Headquarters is now a real choice
  // above, so the empty option has no honest meaning left.
  siteRequired: { de: 'Wählen Sie den Standort.', en: 'Pick the site.' },
  memberHq: { de: 'Mitglied · Hauptverwaltung', en: 'Member · Headquarters' },
  toastUpdated: { de: 'Benutzer aktualisiert', en: 'User updated' },

  deleteTitle: { de: '{name} löschen?', en: 'Delete {name}?' },
  deleteMsgPrefix: { de: 'Dies entfernt', en: 'This permanently removes' },
  deleteMsgSuffix: { de: 'dauerhaft und widerruft den Zugriff.', en: 'and revokes their access.' },
  toastDeleted: { de: 'Benutzer gelöscht', en: 'User deleted' },

  // Enable / disable (R-087)
  statusActive: { de: 'Aktiv', en: 'Active' },
  statusDisabled: { de: 'Deaktiviert', en: 'Disabled' },
  disableUser: { de: 'Deaktivieren', en: 'Disable' },
  enableUser: { de: 'Aktivieren', en: 'Enable' },
  disableTitle: { de: '{name} deaktivieren?', en: 'Disable {name}?' },
  enableTitle: { de: '{name} aktivieren?', en: 'Enable {name}?' },
  disableMsg: {
    de: 'Der Benutzer kann sich nicht mehr anmelden und aktive Sitzungen werden sofort beendet. Umkehrbar.',
    en: 'The user can no longer sign in and any live sessions end immediately. Reversible.',
  },
  enableMsg: {
    de: 'Stellt den Zugriff des Benutzers wieder her — er kann sich wieder anmelden.',
    en: "Restores the user's access — they can sign in again.",
  },
  toastDisabled: { de: 'Benutzer deaktiviert', en: 'User disabled' },
  toastEnabled: { de: 'Benutzer aktiviert', en: 'User enabled' },

  toastResetCopied: { de: 'Reset-Link kopiert', en: 'Reset link copied' },
  userId: { de: 'Benutzer-ID', en: 'User ID' },
  resetLinkLabel: {
    de: 'Passwort-Reset-Link (läuft in 15 Min ab) — an den Benutzer weitergeben',
    en: 'Password reset link (expires in 15 min) — share with the user',
  },

  loadError: { de: 'Benutzer konnten nicht geladen werden', en: "Couldn't load users" },
  emptyTitle: { de: 'Noch keine Benutzer', en: 'No users yet' },
  emptyMsg: {
    de: 'Laden Sie Teammitglieder über die Registrierungsseite ein.',
    en: 'Invite teammates from the Registration page.',
  },
} as const satisfies Namespace;
